import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type { ThemePref } from "../api/types";

const STORAGE_KEY = "helix.theme";

export function resolveTheme(pref: ThemePref): "light" | "dark" {
  if (pref === "light") return "light";
  if (pref === "dark") return "dark";
  // system
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function applyTheme(resolved: "light" | "dark") {
  const root = document.documentElement;
  root.setAttribute("data-theme", resolved);
}

interface ThemeState {
  pref: ThemePref;
  setPref: (pref: ThemePref) => void;
}

const ThemeContext = createContext<ThemeState | undefined>(undefined);

export function ThemeProvider({
  children,
  initialPref,
}: {
  children: ReactNode;
  initialPref?: ThemePref;
}) {
  const stored = (localStorage.getItem(STORAGE_KEY) as ThemePref | null) ?? null;
  const [pref, setPrefState] = useState<ThemePref>(initialPref ?? stored ?? "system");

  const setPref = useCallback((p: ThemePref) => {
    setPrefState(p);
    localStorage.setItem(STORAGE_KEY, p);
  }, []);

  // Apply theme on change, and react to OS theme changes when in "system" mode.
  useEffect(() => {
    applyTheme(resolveTheme(pref));
    if (pref !== "system") return;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = () => applyTheme(resolveTheme("system"));
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, [pref]);

  const value = useMemo<ThemeState>(() => ({ pref, setPref }), [pref, setPref]);
  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeState {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

export const THEME_PREFS: ThemePref[] = ["system", "light", "dark"];
